// frontend/src/pages/OwnerDashboard.js  (replacement)
// Same data layer as before — react-query vehicles, maintenance logs, owner score.
// Only the presentation changed: full-width grid, derived numbers, no emoji.

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { fetchMyVehicles } from '../api/vehicles';
import API_URL from '../api/baseURL';

import useFleetHealth from '../hooks/useFleetHealth';
import StatBar from '../components/OwnerDashboard/StatBar';
import TriagePanel from '../components/OwnerDashboard/TriagePanel';
import ComplianceGauge from '../components/OwnerDashboard/ComplianceGauge';
import AgendaList from '../components/OwnerDashboard/AgendaList';
import FleetTable from '../components/OwnerDashboard/FleetTable';
import FleetMapPanel from '../components/OwnerDashboard/FleetMapPanel';
import WorkshopRatings from '../components/OwnerDashboard/WorkshopRatings';
import { IconPlus } from '../components/icons/Icons';
import { signal } from '../theme/iparc';

import AlertDetailModal from '../components/OwnerDashboard/AlertDetailModal';
import AiInsightsCard from '../components/OwnerDashboard/AiInsightsCard';
import MaintenanceAnalytics from '../components/MaintenanceAnalytics';
import PurchaseOrderForm from './PurchaseOrderForm';
import RateWorkshopModal from '../components/RateWorkshopModal';

const toNum = (v) => { const n = Number((v ?? 0).toString().replace(/[^\d.-]/g, '')); return Number.isFinite(n) ? n : 0; };
const itemCost = (it) => {
  const price = toNum(it?.price ?? it?.unitPrice ?? it?.unit_price);
  const qty = toNum(it?.quantity ?? it?.qty ?? 1) || 1;
  return [toNum(it?.total), toNum(it?.amount), toNum(it?.cost), price * qty].find(x => x > 0) || 0;
};
const sumArray = (a) => Array.isArray(a) ? a.reduce((s, it) => s + itemCost(it), 0) : 0;
const logCost = (log) => {
  const direct = toNum(log?.totalCost ?? log?.total ?? log?.amount ?? log?.cost ?? (toNum(log?.partsCost) + toNum(log?.laborCost)));
  if (direct > 0) return direct;
  return sumArray(log?.items) + sumArray(log?.parts) + sumArray(log?.services)
       + sumArray(log?.operations) + sumArray(log?.lines) + sumArray(log?.entries);
};
const isRelevantLog = (log) => {
  const s = String(log?.status || '').toLowerCase();
  return ['confirmed', 'completed', 'done', 'approved', 'paid'].includes(s) || log?.invoiceId || log?.paid === true;
};

export default function OwnerDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const queryClient = useQueryClient();

  const [vehicleCosts, setVehicleCosts] = useState({});
  const [maintenanceLogs, setMaintenanceLogs] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [pendingRatings, setPendingRatings] = useState([]);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, type: null, vehicles: [] });
  const [showMore, setShowMore] = useState(false);

  const { data: vehicles = [] } = useQuery({ queryKey: ['myVehicles'], queryFn: fetchMyVehicles });
  const health = useFleetHealth(vehicles);

  // ── Maintenance logs → per-vehicle cost ───────────────────────────────────
  useEffect(() => {
    if (!token || vehicles.length === 0) return;
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      const logsMap = {}, costsMap = {};
      await Promise.all(vehicles.map(async (v) => {
        try {
          const res = await axios.get(`${API_URL}/maintenance/vehicle/${v._id}`, { headers });
          const logs = Array.isArray(res.data) ? res.data : [];
          logsMap[v._id] = logs;
          const pool = logs.filter(isRelevantLog);
          costsMap[v._id] = (pool.length ? pool : logs).reduce((s, l) => s + logCost(l), 0);
        } catch { logsMap[v._id] = []; costsMap[v._id] = 0; }
      }));
      setMaintenanceLogs(logsMap);
      setVehicleCosts(costsMap);
    })();
  }, [vehicles, token]);

  // Fallback: sum from invoices
  useEffect(() => {
    if (!token || !vehicles.length) return;
    axios.get(`${API_URL}/invoices/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const byVehicle = {};
        (Array.isArray(res.data) ? res.data : []).forEach(inv => {
          const vId = inv?.vehicle?._id || inv?.vehicle;
          if (vId) byVehicle[vId] = (byVehicle[vId] || 0) + toNum(inv?.total);
        });
        setVehicleCosts(prev => {
          const out = { ...prev };
          Object.entries(byVehicle).forEach(([id, sum]) => { if (!out[id]) out[id] = sum; });
          return out;
        });
      }).catch(() => {});
  }, [token, vehicles]);

  useEffect(() => {
    if (!user?.isCompany) return;
    axios.get(`${API_URL}/users?role=car workshop,spare parts supplier`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setRecipients(res.data)).catch(() => setRecipients([]));
  }, [token, user]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/maintenance/pending-ratings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPendingRatings(res.data)).catch(() => setPendingRatings([]));
  }, [token]);

  // The modal is opened on demand from the ratings panel. It no longer pops up
  // automatically: skipping used to drop the item from local state, so a
  // dismissed rating became unreachable until a page reload. It now stays
  // listed under "Ateliers & évaluations" until it is actually rated.
  const openRating = (maintenance) => {
    setRatingTarget(maintenance);
    setShowRatingModal(true);
  };
  const closeRating = () => {
    setShowRatingModal(false);
    setRatingTarget(null);
  };
  const handleRated = () => {
    const id = ratingTarget?._id;
    setPendingRatings((p) => p.filter((r) => r._id !== id));
    closeRating();
    // Re-sync with the server so the list reflects the persisted `rated` flag.
    axios.get(`${API_URL}/maintenance/pending-ratings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setPendingRatings(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  };

  // ── Derived money ─────────────────────────────────────────────────────────
  const totalCost = useMemo(
    () => Object.values(vehicleCosts).reduce((s, c) => s + c, 0), [vehicleCosts]);

  const spendSeries = useMemo(() => {
    const months = Array(12).fill(0);
    const now = new Date();
    Object.values(maintenanceLogs).flat().forEach(log => {
      const d = new Date(log?.date || log?.createdAt || 0);
      if (Number.isNaN(+d)) return;
      const idx = 11 - ((now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth()));
      if (idx >= 0 && idx < 12) months[idx] += logCost(log);
    });
    return months;
  }, [maintenanceLogs]);

  // Average over the months that actually contain spend, not a blind /12 —
  // dividing a single month of data by 12 understates the real monthly cost.
  const monthlyAvg = useMemo(() => {
    const active = spendSeries.filter((v) => v > 0).length;
    return active ? Math.round(totalCost / active) : 0;
  }, [spendSeries, totalCost]);

  // Real labour/parts split from the maintenance logs (laborCost / partsCost).
  // Anything not attributable to either — a log that only carries a total —
  // is reported as "Autres" instead of being invented. Bars with no data are
  // dropped, so nothing on screen is a guess.
  const costPerKm = useMemo(() => {
    const km = vehicles.reduce((s, v) => s + toNum(v.odometerReading), 0);
    const total = km > 0 ? Math.round(totalCost / km) : 0;

    let labour = 0, parts = 0;
    Object.values(maintenanceLogs).flat().forEach((log) => {
      if (!log) return;
      labour += toNum(log.laborCost);
      parts  += toNum(log.partsCost);
    });
    const attributed = labour + parts;
    const other = Math.max(0, totalCost - attributed);

    const rows = [
      { label: 'Main-d’œuvre', value: labour, color: '#C2BFB6' },
      { label: 'Pièces',       value: parts,  color: '#8A8780' },
      { label: 'Autres',       value: other,  color: '#5C554C' },
    ].filter((r) => r.value > 0);

    const sum = rows.reduce((s, r) => s + r.value, 0);
    return {
      total,
      // value = cost-per-km contribution of that category, so the bars add up
      // to the headline F/km figure above them.
      parts: rows.map((r) => ({
        ...r,
        pct: sum > 0 ? `${Math.round((r.value / sum) * 100)}%` : '0%',
        value: sum > 0 ? Math.round(total * (r.value / sum)) : 0,
      })),
    };
  }, [vehicles, totalCost, maintenanceLogs]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return t('dashboard.greeting.morning');
    if (h < 18) return t('dashboard.greeting.afternoon');
    return t('dashboard.greeting.evening');
  })();

  const firstName = (user?.name || user?.fullName || '').trim().split(/\s+/)[0] || '';

  // ── Actions ───────────────────────────────────────────────────────────────
  const openGroup = (g) => setAlertModal({ show: true, type: g.key, vehicles: g.vehicles });

  const handleAction = (key, group) => {
    if (key === 'oil') return navigate('/book-workshop');
    if (key === 'technical' || key === 'technicalSoon') return navigate('/book-workshop?service=technical');
    if (key === 'insurance') return navigate('/vehicles?filter=insurance');
    if (group) openGroup(group);
  };

  // PATCH, not PUT: the backend only exposes PATCH /vehicles/:id (a PUT would
  // 404). This matches what OdometerUpdateWidget called before the redesign.
  const saveOdometer = async (v, value) => {
    await axios.patch(`${API_URL}/vehicles/${v._id}`, { odometerReading: value },
      { headers: { Authorization: `Bearer ${token}` } });
    // Awaited so the row stays in its saving state until the refreshed value is
    // actually on screen — the odometer drives the vidange bar and cost/km, and
    // both must move together with it.
    await queryClient.invalidateQueries({ queryKey: ['myVehicles'] });
  };

  return (
    <div className="min-h-screen bg-[#0D0C0B] text-[#F4F1EA] font-['IBM_Plex_Sans',system-ui,sans-serif] antialiased">

      {showRatingModal && ratingTarget && (
        <RateWorkshopModal
          maintenance={ratingTarget} token={token}
          onRated={handleRated}
          onClose={closeRating}
        />
      )}
      {alertModal.show && (
        <AlertDetailModal
          type={alertModal.type} vehicles={alertModal.vehicles} navigate={navigate}
          onClose={() => setAlertModal({ show: false, type: null, vehicles: [] })}
        />
      )}

      <main className="w-full max-w-[1420px] px-[30px] pt-[26px] pb-[60px] flex flex-col gap-[18px]">

        <header className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-[11px] uppercase tracking-[.2em] text-[#6E665C] mb-1.5">{greeting}{firstName && ` ${firstName}`}</p>
            <h1 className="font-['Space_Grotesk',sans-serif] text-[30px] font-semibold tracking-[-.025em] leading-[1.1]">
              Votre flotte, en un coup d’œil
            </h1>
          </div>
          <div className="flex gap-2.5">
            <button onClick={() => navigate('/add-vehicle')}
              className="h-[34px] px-3.5 rounded-[9px] bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] hover:from-white hover:to-[#DED9CE]
                         text-[#141210] text-[12.5px] font-semibold flex items-center gap-1.5 transition-colors">
              <IconPlus className="w-3.5 h-3.5" /> Ajouter un véhicule
            </button>
          </div>
        </header>

        {vehicles.length === 0 ? (
          <div className="rounded-2xl border border-[#241F1B] py-16 flex flex-col items-center gap-4">
            <p className="text-[13px] text-[#8F877B]">Aucun véhicule enregistré</p>
            <button onClick={() => navigate('/add-vehicle')}
              className="h-[34px] px-4 rounded-[9px] bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] text-[#141210] text-[12.5px] font-semibold">
              Ajouter mon premier véhicule
            </button>
          </div>
        ) : (
          <>
            <StatBar health={health} totalCost={totalCost} monthlyAvg={monthlyAvg}
                     costPerKm={costPerKm} spendSeries={spendSeries} />

            {/* Where the cars are, without opening a page per car. Renders
                nothing at all when no vehicle has a tracker fitted. */}
            <FleetMapPanel
              token={token}
              onOpenVehicle={(r) => navigate(`/vehicles/${r.vehicleId}`)}
            />

            {/* Two equal-height columns (items-stretch), paired so their natural
                heights are close: the tall variable panel (triage) sits with the
                short agenda, the fixed-height gauge with the ratings list. The
                last panel in each column grows (flex-1) to absorb whatever
                difference is left, so neither column leaves dead space. */}
            <div className="flex flex-wrap gap-3.5 items-stretch">
              <div className="flex-1 min-w-0 basis-[480px] flex flex-col gap-3.5">
                {/* Plain wrapper: neutralises TriagePanel's own flex-1/basis,
                    which would otherwise size it vertically in this column. */}
                <div>
                  <TriagePanel
                    health={health}
                    onOpenGroup={openGroup}
                    onAction={handleAction}
                    onSelectVehicle={(v) => navigate(`/vehicles/${v._id}`)}
                  />
                </div>
                <AgendaList
                  vehicles={vehicles}
                  onSeeCalendar={() => navigate('/vehicles')}
                  className="flex-1"
                />
              </div>
              <div className="flex-1 min-w-0 basis-[316px] flex flex-col gap-3.5">
                <ComplianceGauge health={health} />
                <WorkshopRatings
                  token={token}
                  pending={pendingRatings}
                  onRate={openRating}
                  className="flex-1"
                />
              </div>
            </div>

            <FleetTable
              vehicles={vehicles} health={health}
              onOpenVehicle={(v) => navigate(`/vehicles/${v._id}`)}
              onSaveOdometer={saveOdometer}
              onSeeAll={() => navigate('/vehicles')}
              onAddVehicle={() => navigate('/add-vehicle')}
            />
          </>
        )}

        {/* Secondary analytics — unchanged, still collapsed by default */}
        <section className="pt-1">
          <button onClick={() => setShowMore(s => !s)}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-[12.5px] text-[#6E665C] hover:text-[#C9C3B6] transition-colors">
            {showMore ? 'Masquer' : 'Plus'} — analyses, ateliers, IA{user?.isCompany ? ', bons de commande' : ''}
            <svg className={`w-4 h-4 transition-transform ${showMore ? 'rotate-180' : ''}`}
                 fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showMore && (
            <div className="mt-3.5 grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(340px,1fr))]">
              <Panel title="Analyse IA & diagnostic"><AiInsightsCard vehicles={vehicles} /></Panel>
              <Panel title="Analytiques de maintenance">
                <MaintenanceAnalytics token={token} vehicles={vehicles} maintenanceLogs={maintenanceLogs} />
              </Panel>
              {/* PurchaseOrderForm renders its own <Panel> — don't nest it in another. */}
              {user?.isCompany && <PurchaseOrderForm users={recipients} />}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

const Panel = ({ title, children }) => (
  <div className="rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] overflow-hidden">
    <div className="px-[18px] py-3.5 border-b border-[#241F1B]">
      <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">{title}</p>
    </div>
    <div className="p-[18px]">{children}</div>
  </div>
);
